/**
 * Per-channel table on the connection detail view, 1:1 with QIE channel status:
 * channel, direction, state, message counts, error queue, last message and last error.
 */
import {
  Box,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import type { EhrSyncResource } from '@models/ehr-sync';

import ResourceStateChip from '../../components/ResourceStateChip';
import { directionMessages } from '../../messages';
import RelativeTime from '../shared/RelativeTime';
import { DIRECTION_GLYPH } from '../shared/monitoring.util';

const messages = defineMessages({
  title: {
    id: 'ehrSync.detail.resources.title',
    defaultMessage: 'Channels',
  },
  channel: {
    id: 'ehrSync.detail.resources.column.channel',
    defaultMessage: 'Channel',
  },
  direction: {
    id: 'ehrSync.detail.resources.column.direction',
    defaultMessage: 'Sync direction',
  },
  state: {
    id: 'ehrSync.detail.resources.column.state',
    defaultMessage: 'State',
  },
  received: {
    id: 'ehrSync.detail.resources.column.received',
    defaultMessage: 'Received',
  },
  completed: {
    id: 'ehrSync.detail.resources.column.completed',
    defaultMessage: 'Completed',
  },
  errorQueue: {
    id: 'ehrSync.detail.resources.column.errorQueue',
    defaultMessage: 'Error queue',
  },
  lastMessage: {
    id: 'ehrSync.detail.resources.column.lastMessage',
    defaultMessage: 'Last message',
  },
  lastError: {
    id: 'ehrSync.detail.resources.column.lastError',
    defaultMessage: 'Last error',
  },
  disabled: {
    id: 'ehrSync.detail.resources.disabled',
    defaultMessage: 'Disabled in QIE',
  },
  stoppedNodes: {
    id: 'ehrSync.detail.resources.stoppedNodes',
    defaultMessage: 'Stopped: {nodes}',
  },
});

interface ResourceConfigTableProps {
  resources: EhrSyncResource[];
}

const LEGEND = ['bidirectional', 'inbound', 'outbound'] as const;

export default function ResourceConfigTable({
  resources,
}: ResourceConfigTableProps) {
  const { formatMessage, formatNumber } = useIntl();

  const count = (value?: number | null) =>
    value === null || value === undefined ? '-' : formatNumber(value);

  const headers = [
    messages.channel,
    messages.direction,
    messages.state,
    messages.received,
    messages.completed,
    messages.errorQueue,
    messages.lastMessage,
    messages.lastError,
  ];

  return (
    <Paper variant="outlined">
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        alignItems={{ sm: 'center' }}
        sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Typography variant="subtitle1" fontWeight="bold" sx={{ flex: 1 }}>
          {formatMessage(messages.title)}
        </Typography>
        <Stack direction="row" spacing={1.75} flexWrap="wrap" useFlexGap>
          {LEGEND.map((direction) => (
            <Typography
              key={direction}
              variant="caption"
              fontWeight="bold"
              color="text.secondary"
            >
              <Box component="span" aria-hidden sx={{ color: 'primary.main' }}>
                {DIRECTION_GLYPH[direction]}
              </Box>{' '}
              {formatMessage(directionMessages[direction])}
            </Typography>
          ))}
        </Stack>
      </Stack>

      <TableContainer>
        <Table size="small" sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow>
              {headers.map((header) => (
                <TableCell key={header.id}>
                  <Typography
                    variant="overline"
                    fontWeight="bold"
                    color="text.secondary"
                  >
                    {formatMessage(header)}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {resources.map((resource) => (
              <TableRow key={resource.channelId ?? resource.resourceType}>
                <TableCell>
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    color="text.primary"
                  >
                    {resource.resourceType}
                  </Typography>
                  {resource.channelName !== resource.resourceType ? (
                    <Typography variant="caption" color="text.secondary">
                      {resource.channelName}
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {resource.direction ? (
                      <>
                        <Box
                          component="span"
                          aria-hidden
                          sx={{ color: 'primary.main' }}
                        >
                          {DIRECTION_GLYPH[resource.direction]}
                        </Box>{' '}
                        {formatMessage(directionMessages[resource.direction])}
                      </>
                    ) : (
                      '-'
                    )}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Stack alignItems="flex-start" spacing={0.5}>
                    {resource.state ? (
                      <ResourceStateChip state={resource.state} />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        -
                      </Typography>
                    )}
                    {resource.isEnabled === false ? (
                      <Typography variant="caption" color="text.secondary">
                        {formatMessage(messages.disabled)}
                      </Typography>
                    ) : null}
                    {resource.stoppedNodes?.length ? (
                      <Typography variant="caption" color="error.main">
                        {formatMessage(messages.stoppedNodes, {
                          nodes: resource.stoppedNodes.join(', '),
                        })}
                      </Typography>
                    ) : null}
                  </Stack>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.primary">
                    {count(resource.receivedCount)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.primary">
                    {count(resource.completedCount)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography
                    variant="body2"
                    color={resource.errorQueue ? 'error.main' : 'text.primary'}
                    fontWeight={resource.errorQueue ? 'bold' : 'regular'}
                  >
                    {count(resource.errorQueue)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    <RelativeTime iso={resource.lastReceivedAt} />
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    <RelativeTime iso={resource.lastErrorAt} />
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}
